/* @img-migrated */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Download, FileText, Check, Shield, Palette, Type, Image, Layout,
  Eye, BookOpen, CheckCircle2, XCircle, ArrowRight, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

import Img from '../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_PDF_URL = '/brand/dccf-manual.pdf';
const LOGO_LIGHT = '/brand/logo-color-alt.png';
const LOGO_DARK = '/brand/logo-color.png';
const LOGO_BLACK_BG = '/brand/logo-black-bg.png';

const brandColors = [
  { name: 'AZUL', nameEn: 'Blue', hex: '#002249', rgb: '0, 34, 73', desc: 'Trust, stability, professionalism. Inspired by the Dominican flag.' },
  { name: 'ROJO', nameEn: 'Red', hex: '#CD1125', rgb: '205, 17, 37', desc: 'Energy, dynamism, passion. Symbolizes strength and determination.' },
  { name: 'GRIS', nameEn: 'Grey', hex: '#EFEFEF', rgb: '239, 239, 239', desc: 'Clarity, purity, balance. Visual breathing space.' },
];

const logoAssets = [
  { label: 'Primary Logo — Dark Version', sublabel: 'For white and light backgrounds', bgClass: 'bg-white border border-slate-200', src: LOGO_DARK, format: 'PNG', filename: 'DCCF-Logo-Dark.png' },
  { label: 'Primary Logo — Light Version', sublabel: 'For dark and blue backgrounds', bgClass: 'bg-[#002249]', src: LOGO_LIGHT, format: 'PNG', filename: 'DCCF-Logo-Light.png' },
  { label: 'Full Color Logo — Black Background', sublabel: 'Maps emblem with DCCF wordmark', bgClass: 'bg-black', src: LOGO_BLACK_BG, format: 'PNG', filename: 'DCCF-Logo-Black-BG.png' },
  { label: 'Logo on Red Accent', sublabel: 'For brand-accent and event surfaces', bgClass: 'bg-[#CD1125]', src: LOGO_LIGHT, format: 'PNG', filename: 'DCCF-Logo-Red-Accent.png' },
  { label: 'Logo on Brand Gradient', sublabel: 'Red-to-blue branded gradient', bgClass: 'bg-gradient-to-r from-[#CD1125] to-[#002249]', src: LOGO_LIGHT, format: 'PNG', filename: 'DCCF-Logo-Gradient.png' },
  { label: 'Logo on Navy Dark', sublabel: 'Deep navy institutional background', bgClass: 'bg-[#001530]', src: LOGO_LIGHT, format: 'PNG', filename: 'DCCF-Logo-Navy.png' },
];

const triggerDownload = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'dccf-asset';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab
    window.open(url, '_blank');
  }
};

const useTypes = [
  'Marketing & Promotional', 'Event Materials', 'Digital / Social Media',
  'Print / Publication', 'Partnership / Co-branding', 'Press / Media', 'Internal Use', 'Other',
];

const BrandResourcesPage = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', use_type: 'Marketing & Promotional', intended_use: '', acknowledged_disclaimer: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.acknowledged_disclaimer) { toast.error('Please accept the brand usage terms'); return; }
    if (!form.name || !form.email || !form.company) { toast.error('Please fill in all required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/brand-requests`, form);
      setHasAccess(true);
      toast.success('Access granted! You can now download brand assets.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="brand-resources-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-[#002249]">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-28 sm:pt-36 pb-14 sm:pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[#CD1125] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase mb-4 block">Brand Center</span>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Brand Resources
            </h1>
            <p className="text-white/55 text-sm sm:text-base lg:text-lg max-w-xl leading-relaxed">
              Access the official DCCF brand assets, guidelines, logo files, and color palette. 
              Everything you need to represent the Dominican Chamber of Commerce of Florida.
            </p>
          </motion.div>
        </div>
        <div className="h-1 bg-gradient-to-r from-[#002249] via-[#CD1125] to-[#002249]" />
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {!hasAccess ? (
          <AccessForm form={form} setForm={setForm} submitting={submitting} onSubmit={handleSubmit} />
        ) : (
          <BrandContent />
        )}
      </div>
    </div>
  );
};

/* ==================== ACCESS FORM (gate) ==================== */
const AccessForm = ({ form, setForm, submitting, onSubmit }) => (
  <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
    <div className="lg:col-span-2">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="text-xl sm:text-2xl font-bold text-[#002249] mb-4">Request Brand Access</h2>
        <p className="text-slate-600 text-sm leading-relaxed mb-6">
          Fill out this short form to get immediate access to all DCCF brand assets, including logos, color specs, typography, and the full brand guidelines PDF.
        </p>
        <div className="space-y-4">
          {[
            { icon: Download, text: 'Logo files (multiple versions)' },
            { icon: Palette, text: 'Official color palette with HEX & RGB' },
            { icon: Type, text: 'Typography guidelines' },
            { icon: BookOpen, text: 'Complete brand manual (PDF)' },
            { icon: Layout, text: 'Usage examples & applications' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-slate-700">
              <div className="w-8 h-8 rounded-sm bg-[#002249]/8 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-4 h-4 text-[#002249]" />
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="lg:col-span-3 bg-slate-50 rounded-sm border border-slate-200 p-6 sm:p-8" data-testid="brand-access-form">
      <form onSubmit={onSubmit} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name <span className="text-[#CD1125]">*</span></label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required className="h-11 rounded-sm" data-testid="brand-form-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email <span className="text-[#CD1125]">*</span></label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" required className="h-11 rounded-sm" data-testid="brand-form-email" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (xxx) xxx-xxxx" className="h-11 rounded-sm" data-testid="brand-form-phone" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company <span className="text-[#CD1125]">*</span></label>
            <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Your company name" required className="h-11 rounded-sm" data-testid="brand-form-company" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Type of Use</label>
          <select value={form.use_type} onChange={e => setForm({ ...form, use_type: e.target.value })}
            className="w-full h-11 rounded-sm border border-slate-200 px-3 text-sm focus:border-[#002249] focus:ring-1 focus:ring-[#002249]/10 outline-none bg-white" data-testid="brand-form-use-type">
            {useTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Purpose of Use</label>
          <textarea value={form.intended_use} onChange={e => setForm({ ...form, intended_use: e.target.value })}
            placeholder="Briefly describe how you plan to use the DCCF brand assets..."
            rows={3} className="w-full rounded-sm border border-slate-200 px-3 py-2.5 text-sm focus:border-[#002249] focus:ring-1 focus:ring-[#002249]/10 outline-none resize-none" data-testid="brand-form-purpose" />
        </div>
        {/* Disclaimer checkbox */}
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="brand-disclaimer-gate">
          <div className="flex items-start gap-3">
            <button type="button" onClick={() => setForm({ ...form, acknowledged_disclaimer: !form.acknowledged_disclaimer })}
              className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${form.acknowledged_disclaimer ? 'bg-[#002249] border-[#002249]' : 'border-slate-300 hover:border-slate-400'}`}
              data-testid="brand-disclaimer-check">
              {form.acknowledged_disclaimer && <Check className="w-3 h-3 text-white" />}
            </button>
            <div className="text-[13px] text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-800">I agree to the brand usage terms:</span>
              <ul className="mt-1.5 space-y-1 text-slate-500">
                <li className="flex items-start gap-1.5"><span className="text-[#CD1125] mt-0.5">&#8226;</span> I will respect the official DCCF brand guidelines</li>
                <li className="flex items-start gap-1.5"><span className="text-[#CD1125] mt-0.5">&#8226;</span> Use of these assets does not imply official representation</li>
                <li className="flex items-start gap-1.5"><span className="text-[#CD1125] mt-0.5">&#8226;</span> No unauthorized modifications or misuse of the brand</li>
                <li className="flex items-start gap-1.5"><span className="text-[#CD1125] mt-0.5">&#8226;</span> The Chamber is not responsible for unauthorized use of materials</li>
              </ul>
            </div>
          </div>
        </div>
        <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#002249] hover:bg-[#001630] rounded-sm text-[15px] font-semibold shadow-sm" data-testid="brand-submit-btn">
          {submitting ? 'Processing...' : 'Get Instant Access'}
          {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>
    </motion.div>
  </div>
);

/* ==================== BRAND CONTENT (after access) ==================== */
const BrandContent = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-14 sm:space-y-20">
    {/* Success Banner */}
    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5 flex items-center gap-4" data-testid="brand-access-granted">
      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
      </div>
      <div>
        <p className="text-sm font-semibold text-emerald-800">Access Granted</p>
        <p className="text-xs text-emerald-600">You now have access to all DCCF brand assets. Scroll down to explore and download.</p>
      </div>
    </div>

    {/* Disclaimer */}
    <div className="bg-[#002249]/[0.03] border border-[#002249]/10 rounded-sm p-5 sm:p-6" data-testid="brand-disclaimer">
      <div className="flex gap-4">
        <div className="w-10 h-10 rounded-sm bg-[#002249]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Shield className="w-5 h-5 text-[#002249]" />
        </div>
        <div className="text-[13px] text-slate-600 leading-relaxed space-y-2">
          <h3 className="font-bold text-[#002249] text-sm">Brand Usage Guidelines</h3>
          <p>All brand materials are the property of the Dominican Chamber of Commerce of Florida and must be used respectfully and in alignment with our institutional identity.</p>
          <p>Access to these materials <strong className="text-[#002249]">does not imply official representation</strong>. The DCCF is not responsible for any unauthorized use.</p>
          <p>Assets are intended only for <strong className="text-[#002249]">approved promotions, Chamber-related events, official collaborations, and recognized members</strong>.</p>
        </div>
      </div>
    </div>

        {/* ===== BRAND GUIDELINES PDF — Featured ===== */}
        <Section title="Brand Guidelines" subtitle="Complete Manual" icon={BookOpen}>
          <div
            onClick={() => triggerDownload(BRAND_PDF_URL, 'DCCF-Brand-Manual.pdf')}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-5 bg-[#002249] rounded-sm p-5 sm:p-7 text-white hover:bg-[#001630] transition-colors group cursor-pointer" data-testid="download-pdf">
            <div className="w-16 h-16 rounded-sm bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/15 transition-colors">
              <FileText className="w-8 h-8" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg sm:text-xl">DCCF Brand Manual</p>
              <p className="text-white/55 text-sm mt-1 leading-relaxed">Official brand guidelines including logo construction, color specifications, typography, usage rules, and real-world applications.</p>
            </div>
            <div className="flex items-center gap-2 bg-[#CD1125] hover:bg-[#b00f20] px-5 py-2.5 rounded-sm text-sm font-bold flex-shrink-0 transition-colors" data-testid="pdf-download-btn">
              <Download className="w-4 h-4" /> Download PDF
            </div>
          </div>
        </Section>

        {/* ===== LOGO FILES ===== */}
        <Section title="Logo Files" subtitle="Official Versions — Click to Download" icon={Image}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {logoAssets.map((logo, i) => (
              <LogoCard key={i} {...logo} index={i} />
            ))}
          </div>
        </Section>

        {/* ===== COLOR PALETTE ===== */}
        <Section title="Color Palette" subtitle="Official Brand Colors" icon={Palette}>
          <div className="grid sm:grid-cols-3 gap-5">
            {brandColors.map((c) => (
              <div key={c.hex} className="rounded-sm border border-slate-200 overflow-hidden" data-testid={`color-${c.nameEn.toLowerCase()}`}>
                <div className="h-28 sm:h-32 relative" style={{ backgroundColor: c.hex }}>
                  <span className={`absolute bottom-2 right-3 font-mono text-xs font-bold ${c.hex === '#EFEFEF' ? 'text-[#002249]' : 'text-white/70'}`}>{c.hex}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <h4 className="font-bold text-[#002249] text-[15px]">{c.name}</h4>
                    <span className="text-xs text-slate-400">{c.nameEn}</span>
                  </div>
                  <div className="space-y-1.5 mt-2">
                    <ColorRow label="HEX" value={c.hex} />
                    <ColorRow label="RGB" value={c.rgb} />
                  </div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-sm border border-slate-200 overflow-hidden" data-testid="color-gradient">
            <div className="h-14 bg-gradient-to-r from-[#CD1125] to-[#002249]" />
            <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h4 className="font-bold text-[#002249] text-[15px]">Brand Gradient</h4>
                <p className="text-xs text-slate-500">Official gradient — Red to Blue</p>
              </div>
              <span className="text-xs font-mono text-slate-400 bg-slate-50 px-3 py-1 rounded">#CD1125 → #002249</span>
            </div>
          </div>
        </Section>

        {/* ===== TYPOGRAPHY ===== */}
        <Section title="Typography" subtitle="Montserrat Family" icon={Type}>
          <div className="bg-slate-50 rounded-sm border border-slate-200 p-6 sm:p-8">
            <div className="flex items-baseline justify-between mb-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-[#002249]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Montserrat</h3>
              <span className="text-xs text-slate-400 bg-white px-3 py-1 rounded border border-slate-200">Sans Serif</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">
              A modern geometric sans-serif font with clean, well-defined shapes. Used for all headings, body text, and brand communications.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-widest mb-2">Uppercase</p>
                <p className="text-lg font-bold text-[#002249] tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>A B C D E F G H I J K L M</p>
              </div>
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-widest mb-2">Lowercase</p>
                <p className="text-lg text-[#002249] tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>a b c d e f g h i j k l m</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-[11px] text-slate-400 uppercase tracking-widest mb-2">Numbers</p>
              <p className="text-2xl font-bold text-[#002249] tabular-nums" style={{ fontFamily: "'Montserrat', sans-serif" }}>0 1 2 3 4 5 6 7 8 9</p>
            </div>
          </div>
        </Section>

        {/* ===== LOGO USAGE ===== */}
        <Section title="Logo Usage" subtitle="Do's and Don'ts" icon={Shield}>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { ok: true, text: 'Use on solid brand colors (blue, red, white, black)' },
              { ok: true, text: 'Maintain original proportions at all times' },
              { ok: true, text: 'Use the correct version for each background' },
              { ok: true, text: 'Keep sufficient clear space around the logo' },
              { ok: false, text: 'Do not stretch, distort, or rotate the logo' },
              { ok: false, text: 'Do not change the official brand colors' },
              { ok: false, text: 'Do not place on busy or cluttered backgrounds' },
              { ok: false, text: 'Do not add effects (shadow, glow, outline)' },
            ].map((rule, i) => (
              <div key={i} className={`flex items-start gap-3 p-4 rounded-sm border ${rule.ok ? 'bg-emerald-50/50 border-emerald-200' : 'bg-red-50/50 border-red-200'}`} data-testid={`usage-rule-${i}`}>
                {rule.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
                <p className={`text-sm ${rule.ok ? 'text-emerald-800' : 'text-red-800'}`}>{rule.text}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ===== BRAND VALUES ===== */}
        <Section title="Brand Values" subtitle="The principles that guide us" icon={Eye}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'Colaboracion', titleEn: 'Collaboration', color: '#002249' },
              { title: 'Identidad', titleEn: 'Identity', color: '#CD1125' },
              { title: 'Innovacion', titleEn: 'Innovation', color: '#002249' },
              { title: 'Compromiso', titleEn: 'Commitment', color: '#CD1125' },
              { title: 'Solidaridad', titleEn: 'Solidarity & Trust', color: '#CD1125' },
              { title: 'Diversidad', titleEn: 'Diversity', color: '#002249' },
              { title: 'Union', titleEn: 'Unity & Excellence', color: '#CD1125' },
              { title: 'Responsabilidad', titleEn: 'Social Responsibility', color: '#002249' },
            ].map((v, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center" data-testid={`brand-value-${i}`}>
                <div className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: v.color + '12' }}>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: v.color }} />
                </div>
                <h4 className="font-bold text-[#002249] text-sm">{v.title}</h4>
                <p className="text-[11px] text-slate-500 mt-0.5">{v.titleEn}</p>
              </div>
            ))}
          </div>
        </Section>
      </motion.div>
    );

/* ==================== REUSABLE COMPONENTS ==================== */
const Section = ({ title, subtitle, icon: Icon, children }) => (
  <section data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-6">
      <div className="w-9 h-9 rounded-sm bg-[#002249]/8 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#002249]" />
      </div>
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-[#002249] leading-tight">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const LogoCard = ({ label, sublabel, bgClass, src, format, filename, index }) => (
  <div className="rounded-sm border border-slate-200 overflow-hidden group" data-testid={`logo-asset-${index}`}>
    <div className={`${bgClass} h-40 sm:h-44 flex items-center justify-center p-6 relative`}>
      <Img variant="generic" src={src} alt={label} className="max-h-full max-w-[180px] object-contain" loading="lazy" />
    </div>
    <div className="p-4 bg-white space-y-3">
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-[#002249] truncate">{label}</p>
        <p className="text-[11px] text-slate-400 truncate">{sublabel}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{format}</span>
      </div>
      <button
        onClick={() => triggerDownload(src, filename)}
        className="flex items-center justify-center gap-2 w-full bg-[#002249] text-white py-2.5 rounded-sm text-xs font-bold hover:bg-[#001630] active:scale-[0.98] transition-all"
        data-testid={`download-logo-${index}`}
      >
        <Download className="w-3.5 h-3.5" /> Download {format}
      </button>
    </div>
  </div>
);

const ColorRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-slate-400 font-medium">{label}</span>
    <span className="font-mono text-slate-700 bg-slate-50 px-2 py-0.5 rounded">{value}</span>
  </div>
);

export default BrandResourcesPage;
